import { useContext, useEffect, useMemo, useState } from "react";
import { ActionIcon, Alert, Button, Center, Divider, Grid, Group, Loader, Paper, Popover, Select, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { IconAlertCircle, IconArrowsMaximize } from "@tabler/icons-react";
import { collectionGroup, onSnapshot, query, where } from "firebase/firestore";
import { PieChart } from "@mui/x-charts/PieChart";
import { Margin, usePDF } from "react-to-pdf";
import { ConfigContext, MyUserDataContext } from "../../contexts";
import { useFirestore } from "../../helpers/firebaseContext";
import { GLOBAL_DPM_PORTFOLIO_DB_ID, riskToleranceLabel } from "../../portfolio_done_summary";
import { CustomClaims, RiskLevel, Service } from "../../types";
import {
    AmountByCurrency, Bucket, CountRow, MANAGEMENT_LABELS, PaidSplitRow, Period, PieDatum, SegmentRow, Stage, TREND_MONTHS,
    addAmounts, executionDate, formatAmount, formatAmounts, formatCount, getClientId, getPortfolioFallbackName,
    getSegment, getStage, monthKey, newBucket, newPaidSplitRow, periodStart, requestAmounts, requestDate,
} from "./portfolio_requests_progress_helpers";

/*  Portfolio requests performance (home → "Portfolio requests" tab)
    – The portfolio counterpart of the closed-ended fundraising page: every
      services/{id} doc with serviceType "portfolio" (equities, sukuk, Global
      DPM, …) grouped by segment and stage (in progress / executed / rejected),
      with amounts kept per currency because requests are opened in KWD or USD.
*/

const LAST_SELECTED_PORTFOLIO_KEY = "portfolioRequestsProgress:lastPortfolio";
const LAST_SELECTED_PERIOD_KEY = "portfolioRequestsProgress:lastPeriod";
const ALL_PORTFOLIOS = "__all__";
const CHART_COLORS = ["#C5A548", "#550000", "#545E75", "#7FB7BE", "#262A10", "#D84A05", "#30C5FF", "#CED3DC", "#12b886"];

const PERIOD_OPTIONS: { value: Period; label: string }[] = [
    { value: "all", label: "All time" },
    { value: "this_month", label: "This month" },
    { value: "last_3_months", label: "Last 3 months" },
    { value: "ytd", label: "Year to date" },
    { value: "last_12_months", label: "Last 12 months" },
];

const PortfolioRequestsProgress = () => {
    const db = useFirestore();
    const appContext = useContext(MyUserDataContext);
    const config = useContext(ConfigContext);
    const userClaims = appContext.firebaseTokenResult?.claims.customClaims as CustomClaims;

    const isAdmin = !!userClaims?.admin || !!userClaims?.cr;
    const isRestrictedRM = !!userClaims?.kfh_oper && !isAdmin;
    const restrictedSegment = isRestrictedRM ? userClaims?.segment : undefined;

    const [services, setServices] = useState<Service[]>([]);
    const [servicesLoading, setServicesLoading] = useState(true);
    const [servicesError, setServicesError] = useState<string | null>(null);
    const [selectedPortfolio, setSelectedPortfolio] = useState<string>(() => localStorage.getItem(LAST_SELECTED_PORTFOLIO_KEY) || ALL_PORTFOLIOS);
    const [period, setPeriod] = useState<Period>(() => {
        const saved = localStorage.getItem(LAST_SELECTED_PERIOD_KEY) as Period | null;
        return saved && PERIOD_OPTIONS.some((option) => option.value === saved) ? saved : "all";
    });
    const { toPDF, targetRef } = usePDF({ filename: "portfolio_requests_progress.pdf", page: { margin: Margin.MEDIUM } });

    useEffect(() => {
        setServicesLoading(true);
        setServicesError(null);
        // No orderBy: ordering on "created" would silently drop docs that lack the field.
        const servicesQuery = query(collectionGroup(db, "services"), where("serviceType", "==", "portfolio"));
        const unsubscribe = onSnapshot(
            servicesQuery,
            (snapshot) => {
                setServices(snapshot.docs.map((docSnapshot) => {
                    const data = docSnapshot.data() as Service;
                    return { ...data, id: data.id || docSnapshot.id };
                }));
                setServicesLoading(false);
            },
            (error) => {
                console.error("Error fetching portfolio requests", error);
                setServices([]);
                setServicesLoading(false);
                setServicesError("Could not load portfolio requests right now.");
            }
        );
        return () => unsubscribe();
    }, [db]);

    useEffect(() => {
        localStorage.setItem(LAST_SELECTED_PORTFOLIO_KEY, selectedPortfolio);
    }, [selectedPortfolio]);

    useEffect(() => {
        localStorage.setItem(LAST_SELECTED_PERIOD_KEY, period);
    }, [period]);

    const portfolioNames = useMemo(() => {
        const names = new Map<string, string>();
        Object.entries(config.portfolio_service ?? {})
            .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
            .forEach(([key, entry]) => names.set(key, entry.name_en?.trim() || getPortfolioFallbackName(key)));
        services.forEach((service) => {
            const key = service.portfolioOpeningRequest?.portfolio_db_id?.trim();
            if (key && !names.has(key)) names.set(key, getPortfolioFallbackName(key));
        });
        return names;
    }, [config.portfolio_service, services]);

    const portfolioOptions = useMemo(() => [
        { value: ALL_PORTFOLIOS, label: "All portfolio types" },
        ...Array.from(portfolioNames.entries()).map(([value, label]) => ({ value, label })),
    ], [portfolioNames]);

    useEffect(() => {
        if (selectedPortfolio !== ALL_PORTFOLIOS && !servicesLoading && !portfolioNames.has(selectedPortfolio)) {
            setSelectedPortfolio(ALL_PORTFOLIOS);
        }
    }, [selectedPortfolio, portfolioNames, servicesLoading]);

    const riskLevels = useMemo<RiskLevel[] | undefined>(
        () => config.portfolio_service?.[GLOBAL_DPM_PORTFOLIO_DB_ID]?.management_options?.[0]?.risk_levels,
        [config.portfolio_service]
    );

    const portfolioName = (service: Service) => {
        const key = service.portfolioOpeningRequest?.portfolio_db_id?.trim();
        return key ? portfolioNames.get(key) || getPortfolioFallbackName(key) : "Unspecified";
    };

    // Drafts are counted, matching the services Excel export; segment-restricted RMs only see their own segment, as on the fundraising page.
    const visibleServices = useMemo(() => services.filter((service) => {
        if (restrictedSegment && getSegment(service) !== restrictedSegment) return false;
        if (selectedPortfolio !== ALL_PORTFOLIOS && service.portfolioOpeningRequest?.portfolio_db_id?.trim() !== selectedPortfolio) return false;
        return true;
    }), [services, restrictedSegment, selectedPortfolio]);

    const periodServices = useMemo(() => {
        const start = periodStart(period);
        if (!start) return visibleServices;
        return visibleServices.filter((service) => {
            const date = requestDate(service);
            return !!date && date >= start;
        });
    }, [visibleServices, period]);

    const aggregate = useMemo(() => {
        const stages: Record<Stage, Bucket> = { in_progress: newBucket(), executed: newBucket(), rejected: newBucket() };
        const paid = newBucket();
        const executedPaid = newBucket();
        const manual = newBucket();
        const digital = newBucket();
        const requested: AmountByCurrency = {};
        const allClients = new Set<string>();

        const segments = new Map<string, SegmentRow & { clientSet: Set<string> }>();
        const stageSegments: Record<Stage, Map<string, Bucket>> = { in_progress: new Map(), executed: new Map(), rejected: new Map() };
        const segmentPaid = new Map<string, AmountByCurrency>(); // executed and paid, per segment (Executed table)
        const statusCounts = new Map<string, CountRow>();
        const typeCounts = new Map<string, CountRow>();
        const managementCounts = new Map<string, CountRow>();
        const riskCounts = new Map<string, CountRow>();
        // Paid vs not paid pivots (every status, rejected included) by stage and by segment.
        const paidByStatus = new Map<string, PaidSplitRow>();
        const paidBySegment = new Map<string, PaidSplitRow>();
        const paidSplitTotals = newPaidSplitRow("Total");

        let executionDays = 0;
        let executionSamples = 0;

        const addToBucket = (bucket: Bucket, clientId: string, amount: AmountByCurrency) => {
            bucket.requests += 1;
            bucket.clients.add(clientId);
            addAmounts(bucket.amount, amount);
        };
        const addToMap = (map: Map<string, Bucket>, key: string, clientId: string, amount: AmountByCurrency) => {
            if (!map.has(key)) map.set(key, newBucket());
            addToBucket(map.get(key)!, clientId, amount);
        };
        const addSplit = (row: PaidSplitRow, isPaid: boolean, clientId: string, amount: AmountByCurrency) => {
            addToBucket(isPaid ? row.paid : row.notPaid, clientId, amount);
            addToBucket(row.total, clientId, amount);
        };
        const splitRow = (map: Map<string, PaidSplitRow>, label: string) => {
            if (!map.has(label)) map.set(label, newPaidSplitRow(label));
            return map.get(label)!;
        };
        const countRow = (map: Map<string, CountRow>, label: string, clientId: string) => {
            if (!map.has(label)) map.set(label, { label, requests: 0, clients: new Set() });
            const row = map.get(label)!;
            row.requests += 1;
            row.clients.add(clientId);
        };

        periodServices.forEach((service) => {
            const stage = getStage(service);
            const amount = requestAmounts(service);
            const clientId = getClientId(service);
            const segment = getSegment(service);
            const isPaid = service.tags?.paid?.code === "paid";
            const statusTitle = service.tags?.serviceStatus?.title || service.tags?.serviceStatus?.code || "Unknown";
            const managementType = service.portfolioOpeningRequest?.portfolioType?.trim().toLowerCase();
            const riskTolerance = riskToleranceLabel(service.portfolioOpeningRequest?.riskTolerance, service.portfolioOpeningRequest?.portfolio_db_id, riskLevels);

            allClients.add(clientId);
            addToBucket(stages[stage], clientId, amount);
            addToMap(stageSegments[stage], segment, clientId, amount);
            countRow(statusCounts, statusTitle, clientId);
            countRow(typeCounts, portfolioName(service), clientId);
            if (managementType) countRow(managementCounts, MANAGEMENT_LABELS[managementType] ?? managementType, clientId);
            if (riskTolerance) countRow(riskCounts, riskTolerance, clientId);
            addSplit(splitRow(paidByStatus, statusTitle), isPaid, clientId, amount);
            addSplit(splitRow(paidBySegment, segment), isPaid, clientId, amount);
            addSplit(paidSplitTotals, isPaid, clientId, amount);

            if (!segments.has(segment)) {
                segments.set(segment, { segment, requests: 0, clients: 0, inProgress: 0, executed: 0, rejected: 0, amount: {}, paidAmount: {}, clientSet: new Set() });
            }
            const row = segments.get(segment)!;
            row.requests += 1;
            row.clientSet.add(clientId);
            if (stage === "in_progress") row.inProgress += 1;
            if (stage === "executed") row.executed += 1;
            if (stage === "rejected") row.rejected += 1;

            if (stage === "rejected") return;

            addAmounts(requested, amount);
            addAmounts(row.amount, amount);
            addToBucket(service.manual ? manual : digital, clientId, amount);
            if (isPaid) {
                addToBucket(paid, clientId, amount);
                addAmounts(row.paidAmount, amount);
            }
            if (stage === "executed") {
                if (isPaid) {
                    addToBucket(executedPaid, clientId, amount);
                    if (!segmentPaid.has(segment)) segmentPaid.set(segment, {});
                    addAmounts(segmentPaid.get(segment)!, amount);
                }
                const submitted = requestDate(service);
                const executed = executionDate(service);
                if (submitted && executed && executed >= submitted) {
                    executionDays += (executed.getTime() - submitted.getTime()) / 86_400_000;
                    executionSamples += 1;
                }
            }
        });

        const subtractAmounts = (a: AmountByCurrency, b: AmountByCurrency): AmountByCurrency => {
            const result: AmountByCurrency = { ...a };
            Object.entries(b).forEach(([currency, amount]) => { result[currency] = (result[currency] || 0) - amount; });
            return result;
        };

        const stageRows = (stage: Stage) => Array.from(stageSegments[stage].entries())
            .map(([segment, bucket]) => ({ segment, requests: bucket.requests, clients: bucket.clients.size, amount: bucket.amount, paidAmount: segmentPaid.get(segment) ?? {} }))
            .sort((a, b) => b.requests - a.requests);

        const segmentRows: SegmentRow[] = Array.from(segments.values())
            .map(({ clientSet, ...row }) => ({ ...row, clients: clientSet.size }))
            .sort((a, b) => b.requests - a.requests);

        const countRows = (map: Map<string, CountRow>) => Array.from(map.values()).sort((a, b) => b.requests - a.requests);
        const totalRequests = periodServices.length;

        return {
            inProgressRows: stageRows("in_progress"),
            executedRows: stageRows("executed"),
            segmentRows,
            statusRows: countRows(statusCounts),
            typeRows: countRows(typeCounts),
            managementRows: countRows(managementCounts),
            riskRows: countRows(riskCounts),
            paidByStatusRows: Array.from(paidByStatus.values()).sort((a, b) => b.total.requests - a.total.requests),
            paidBySegmentRows: Array.from(paidBySegment.values()).sort((a, b) => b.total.requests - a.total.requests),
            paidSplitTotals,
            totals: {
                requests: totalRequests,
                clients: allClients.size,
                inProgress: stages.in_progress.requests,
                executed: stages.executed.requests,
                rejected: stages.rejected.requests,
                inProgressClients: stages.in_progress.clients.size,
                executedClients: stages.executed.clients.size,
                rejectedClients: stages.rejected.clients.size,
                paidClients: paid.clients.size,
                requestedAmount: requested,
                inProgressAmount: stages.in_progress.amount,
                executedAmount: stages.executed.amount,
                executedUnpaidAmount: subtractAmounts(stages.executed.amount, executedPaid.amount),
                rejectedAmount: stages.rejected.amount,
                paidAmount: paid.amount,
                executedPaidAmount: executedPaid.amount,
                manualAmount: manual.amount,
                digitalAmount: digital.amount,
                manualRequests: manual.requests,
                digitalRequests: digital.requests,
                conversion: totalRequests ? (stages.executed.requests / totalRequests) * 100 : 0,
                rejectionRate: totalRequests ? (stages.rejected.requests / totalRequests) * 100 : 0,
                averageExecutionDays: executionSamples ? executionDays / executionSamples : undefined,
            },
        };
        // portfolioName only depends on portfolioNames, which is already a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodServices, riskLevels, portfolioNames]);

    // Submitted vs executed per month over the last 12 months, for the selected portfolio type regardless of the period filter.
    const monthlyTrend = useMemo(() => {
        const now = new Date();
        const months: { key: string; label: string }[] = [];
        for (let offset = TREND_MONTHS - 1; offset >= 0; offset -= 1) {
            const date = new Date(now.getFullYear(), now.getMonth() - offset, 1);
            months.push({ key: monthKey(date), label: date.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }) });
        }
        const submitted = new Map<string, number>();
        const executed = new Map<string, number>();
        visibleServices.forEach((service) => {
            const submittedOn = requestDate(service);
            if (submittedOn) submitted.set(monthKey(submittedOn), (submitted.get(monthKey(submittedOn)) || 0) + 1);
            if (getStage(service) === "executed") {
                const executedOn = executionDate(service);
                if (executedOn) executed.set(monthKey(executedOn), (executed.get(monthKey(executedOn)) || 0) + 1);
            }
        });
        return {
            labels: months.map((month) => month.label),
            submitted: months.map((month) => submitted.get(month.key) || 0),
            executed: months.map((month) => executed.get(month.key) || 0),
        };
    }, [visibleServices]);

    const currencies = useMemo(() => {
        const set = new Set<string>();
        [aggregate.totals.requestedAmount, aggregate.totals.rejectedAmount].forEach((amounts) => Object.keys(amounts).forEach((currency) => set.add(currency)));
        return Array.from(set).sort();
    }, [aggregate.totals.requestedAmount, aggregate.totals.rejectedAmount]);

    const toPie = (rows: CountRow[], useClients = false): PieDatum[] =>
        rows.map((row, index) => ({ id: index, value: useClients ? row.clients.size : row.requests, label: row.label }));

    const renderStageTable = (title: string, rows: { segment: string; requests: number; clients: number; amount: AmountByCurrency; paidAmount: AmountByCurrency }[], showPaid: boolean, totals: { requests: number; clients: number; amount: AmountByCurrency; paidAmount?: AmountByCurrency }) => (
        <Paper shadow="sm" radius="md" p="md">
            <Title order={4} mb="xs">{title}</Title>
            <Table striped highlightOnHover withRowBorders={false} horizontalSpacing="sm" verticalSpacing="sm">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Segment</Table.Th>
                        <Table.Th>Requests (#)</Table.Th>
                        <Table.Th>Clients (#)</Table.Th>
                        <Table.Th>Amount</Table.Th>
                        {showPaid && <Table.Th>Paid</Table.Th>}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {rows.length ? rows.map((row) => (
                        <Table.Tr key={row.segment}>
                            <Table.Td><Text fw={500}>{row.segment}</Text></Table.Td>
                            <Table.Td>{row.requests}</Table.Td>
                            <Table.Td>{row.clients}</Table.Td>
                            <Table.Td>{formatAmounts(row.amount)}</Table.Td>
                            {showPaid && <Table.Td>{formatAmounts(row.paidAmount)}</Table.Td>}
                        </Table.Tr>
                    )) : (
                        <Table.Tr>
                            <Table.Td colSpan={showPaid ? 5 : 4}><Text c="dimmed">No data yet</Text></Table.Td>
                        </Table.Tr>
                    )}
                    <Table.Tr>
                        <Table.Td><Text fw={700}>Total</Text></Table.Td>
                        <Table.Td><Text fw={700}>{totals.requests}</Text></Table.Td>
                        <Table.Td><Text fw={700}>{totals.clients}</Text></Table.Td>
                        <Table.Td><Text fw={700}>{formatAmounts(totals.amount)}</Text></Table.Td>
                        {showPaid && <Table.Td><Text fw={700}>{formatAmounts(totals.paidAmount ?? {})}</Text></Table.Td>}
                    </Table.Tr>
                </Table.Tbody>
            </Table>
        </Paper>
    );

    const renderSummaryTable = () => {
        const { totals } = aggregate;
        const conversion = (executed: number, requests: number) => (requests ? `${((executed / requests) * 100).toFixed(2)}%` : "--");
        return (
            <Paper shadow="sm" radius="md" p="md">
                <Title order={4} mb="xs">All requests</Title>
                <Table striped highlightOnHover withRowBorders={false} horizontalSpacing="sm" verticalSpacing="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Segment</Table.Th>
                            <Table.Th>Requests (#)</Table.Th>
                            <Table.Th>Clients (#)</Table.Th>
                            <Table.Th>In progress</Table.Th>
                            <Table.Th>Executed</Table.Th>
                            <Table.Th>Rejected</Table.Th>
                            <Table.Th>Amount</Table.Th>
                            <Table.Th>Paid</Table.Th>
                            <Table.Th>Conversion</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {aggregate.segmentRows.length ? aggregate.segmentRows.map((row) => (
                            <Table.Tr key={row.segment}>
                                <Table.Td><Text fw={500}>{row.segment}</Text></Table.Td>
                                <Table.Td>{row.requests}</Table.Td>
                                <Table.Td>{row.clients}</Table.Td>
                                <Table.Td>{row.inProgress}</Table.Td>
                                <Table.Td>{row.executed}</Table.Td>
                                <Table.Td>{row.rejected}</Table.Td>
                                <Table.Td>{formatAmounts(row.amount)}</Table.Td>
                                <Table.Td>{formatAmounts(row.paidAmount)}</Table.Td>
                                <Table.Td>{conversion(row.executed, row.requests)}</Table.Td>
                            </Table.Tr>
                        )) : (
                            <Table.Tr>
                                <Table.Td colSpan={9}><Text c="dimmed">No data yet</Text></Table.Td>
                            </Table.Tr>
                        )}
                        <Table.Tr>
                            <Table.Td><Text fw={700}>Total</Text></Table.Td>
                            <Table.Td><Text fw={700}>{totals.requests}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{totals.clients}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{totals.inProgress}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{totals.executed}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{totals.rejected}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{formatAmounts(totals.requestedAmount)}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{formatAmounts(totals.paidAmount)}</Text></Table.Td>
                            <Table.Td><Text fw={700}>{conversion(totals.executed, totals.requests)}</Text></Table.Td>
                        </Table.Tr>
                    </Table.Tbody>
                </Table>
            </Paper>
        );
    };

    const renderPaidSplitTable = (title: string, firstColumn: string, rows: PaidSplitRow[], totals: PaidSplitRow) => {
        const bucketCells = (bucket: Bucket, bold = false) => (
            <>
                <Table.Td><Text fw={bold ? 700 : undefined}>{bucket.requests || "--"}</Text></Table.Td>
                <Table.Td><Text fw={bold ? 700 : undefined}>{bucket.clients.size || "--"}</Text></Table.Td>
                <Table.Td><Text fw={bold ? 700 : undefined}>{formatAmounts(bucket.amount)}</Text></Table.Td>
            </>
        );
        return (
            <Paper shadow="sm" radius="md" p="md">
                <Title order={4} mb="xs">{title}</Title>
                <Table striped highlightOnHover withRowBorders={false} horizontalSpacing="sm" verticalSpacing="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th></Table.Th>
                            <Table.Th colSpan={3} ta="center">Not Paid</Table.Th>
                            <Table.Th colSpan={3} ta="center">Paid</Table.Th>
                            <Table.Th colSpan={3} ta="center">Total</Table.Th>
                        </Table.Tr>
                        <Table.Tr>
                            <Table.Th>{firstColumn}</Table.Th>
                            {["Requests (#)", "Clients (#)", "Amount", "Requests (#)", "Clients (#)", "Amount", "Requests (#)", "Clients (#)", "Amount"].map((header, index) => (
                                <Table.Th key={index}>{header}</Table.Th>
                            ))}
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {rows.length ? rows.map((row) => (
                            <Table.Tr key={row.label}>
                                <Table.Td><Text fw={500}>{row.label}</Text></Table.Td>
                                {bucketCells(row.notPaid)}
                                {bucketCells(row.paid)}
                                {bucketCells(row.total)}
                            </Table.Tr>
                        )) : (
                            <Table.Tr>
                                <Table.Td colSpan={10}><Text c="dimmed">No data yet</Text></Table.Td>
                            </Table.Tr>
                        )}
                        <Table.Tr>
                            <Table.Td><Text fw={700}>Grand Total</Text></Table.Td>
                            {bucketCells(totals.notPaid, true)}
                            {bucketCells(totals.paid, true)}
                            {bucketCells(totals.total, true)}
                        </Table.Tr>
                    </Table.Tbody>
                </Table>
            </Paper>
        );
    };

    const renderPie = (title: string, data: PieDatum[], valueFormatter: (value: number) => string) => {
        const total = data.reduce((acc, item) => acc + item.value, 0);
        return (
            <Paper shadow="sm" radius="md" p="md" key={title}>
                <Group justify="space-between" mb="xs">
                    <Title order={5}>{title}</Title>
                    {total > 0 && <Text size="sm" c="dimmed">{valueFormatter(total)}</Text>}
                </Group>
                {total > 0 ? (
                    <PieChart
                        series={[{
                            data: data.filter((item) => item.value > 0),
                            valueFormatter: (item) => valueFormatter(item.value),
                            arcLabel: (item) => {
                                const percentage = total ? (item.value / total) * 100 : 0;
                                return percentage >= 8 ? `${percentage.toFixed(0)}%` : "";
                            },
                            arcLabelMinAngle: 20,
                        }]}
                        height={260}
                        colors={CHART_COLORS}
                        margin={{ top: 10, bottom: 10, left: 0, right: 0 }}
                    />
                ) : (
                    <Center h={220}><Text c="dimmed">No data yet</Text></Center>
                )}
            </Paper>
        );
    };

    const renderTile = (label: string, value: string | number, amounts?: AmountByCurrency) => (
        <Paper p="md" shadow="sm" radius="md">
            <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text c="dimmed" fw={600} size="xs">{label}</Text>
                {amounts && (
                    <Popover position="bottom-end" withArrow shadow="md" withinPortal>
                        <Popover.Target>
                            <ActionIcon variant="subtle" color="gray" size="sm" aria-label={`Show full ${label.toLowerCase()}`} title="Show full amount" data-html2canvas-ignore>
                                <IconArrowsMaximize size={14} />
                            </ActionIcon>
                        </Popover.Target>
                        <Popover.Dropdown style={{ maxWidth: "calc(100vw - 32px)" }}>
                            <Text size="xs" c="dimmed" mb={4}>{label}</Text>
                            <Stack gap={4}>
                                {Object.entries(amounts).filter(([, amount]) => amount).sort(([a], [b]) => a.localeCompare(b)).map(([currency, amount]) => (
                                    <Text key={currency} fw={600} style={{ overflowWrap: "anywhere" }}>
                                        {amount.toLocaleString("en-US", { maximumSignificantDigits: 21 })} {currency}
                                    </Text>
                                ))}
                                {!Object.values(amounts).some((amount) => amount) && <Text>--</Text>}
                            </Stack>
                        </Popover.Dropdown>
                    </Popover>
                )}
            </Group>
            <Title order={2}>{value}</Title>
        </Paper>
    );

    const amountFormatter = (currency: string) => (value: number) => formatAmount(value, currency);
    const requestsFormatter = (value: number) => `${formatCount(value)} requests`;
    const clientsFormatter = (value: number) => `${formatCount(value)} clients`;

    const headerTitle = selectedPortfolio === ALL_PORTFOLIOS
        ? "Portfolio requests performance"
        : `${portfolioNames.get(selectedPortfolio) ?? getPortfolioFallbackName(selectedPortfolio)} Sales Progress`;

    if (servicesLoading && !services.length) {
        return <Center py="xl"><Loader /></Center>;
    }

    return (
        <Stack gap="md" style={{ width: "100%" }}>
            <Group justify="space-between" align="flex-start">
                <div>
                    <Title order={3}>{headerTitle}</Title>
                    <Text c="dimmed" size="sm">
                        Tracking portfolio opening requests, drafts included, by stage: in progress, executed on Geneva, or rejected.
                    </Text>
                    <Text c="dimmed" size="sm">
                        {new Date().toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                    </Text>
                </div>
                <Group>
                    <Select
                        w={260}
                        label="Portfolio type"
                        value={selectedPortfolio}
                        onChange={(value) => setSelectedPortfolio(value || ALL_PORTFOLIOS)}
                        data={portfolioOptions}
                        allowDeselect={false}
                    />
                    <Select
                        w={180}
                        label="Period"
                        value={period}
                        onChange={(value) => setPeriod((value as Period) || "all")}
                        data={PERIOD_OPTIONS}
                        allowDeselect={false}
                    />
                    <Button mt="sm" onClick={() => toPDF()}>Download PDF</Button>
                </Group>
            </Group>

            {servicesError && (
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                    {servicesError}
                </Alert>
            )}

            <Stack ref={targetRef} gap="md" style={{ width: "100%" }}>
                <SimpleGrid cols={{ base: 1, md: 3, lg: 4 }}>
                    {renderTile("Demand (#)", aggregate.totals.requests)}
                    {/* {renderTile("Clients (#)", aggregate.totals.clients)} */}
                    {renderTile("Executed (#)", aggregate.totals.executed)}
                    {renderTile("In progress (#)", aggregate.totals.inProgress)}
                    {renderTile("Rejected (#)", aggregate.totals.rejected)}
                    {renderTile("Demand amount", formatAmounts(aggregate.totals.requestedAmount), aggregate.totals.requestedAmount)}
                    {renderTile("Executed amount", formatAmounts(aggregate.totals.executedAmount), aggregate.totals.executedAmount)}
                    {renderTile("Paid amount", formatAmounts(aggregate.totals.paidAmount), aggregate.totals.paidAmount)}
                    {renderTile("Rejected amount", formatAmounts(aggregate.totals.rejectedAmount), aggregate.totals.rejectedAmount)}
                    {/* {renderTile("Avg. days to execute", aggregate.totals.averageExecutionDays !== undefined ? aggregate.totals.averageExecutionDays.toFixed(1) : "--")} */}
                </SimpleGrid>

                {servicesLoading && <Center><Loader /></Center>}

                {/* <Paper shadow="sm" radius="md" p="md">
                    <Group justify="space-between" mb="xs">
                        <Title order={5}>Monthly trend</Title>
                        <Text size="sm" c="dimmed">Submitted vs executed, last {TREND_MONTHS} months (all periods)</Text>
                    </Group>
                    <BarChart
                        xAxis={[{ scaleType: "band", data: monthlyTrend.labels }]}
                        series={[
                            { data: monthlyTrend.submitted, label: "Submitted", valueFormatter: (value) => requestsFormatter(value ?? 0) },
                            { data: monthlyTrend.executed, label: "Executed", valueFormatter: (value) => requestsFormatter(value ?? 0) },
                        ]}
                        colors={[CHART_COLORS[2], CHART_COLORS[0]]}
                        height={260}
                        margin={{ top: 10, bottom: 10, left: 0, right: 0 }}
                    />
                </Paper> */}


                <Grid gap="md">

                    <Grid.Col span={{ base: 12, lg: 7 }}>
                        <Stack gap="md">
                            {renderStageTable("In progress", aggregate.inProgressRows, false, {
                                requests: aggregate.totals.inProgress,
                                clients: aggregate.totals.inProgressClients,
                                amount: aggregate.totals.inProgressAmount,
                            })}
                            {renderStageTable("Executed", aggregate.executedRows, true, {
                                requests: aggregate.totals.executed,
                                clients: aggregate.totals.executedClients,
                                amount: aggregate.totals.executedAmount,
                                paidAmount: aggregate.totals.executedPaidAmount,
                            })}
                            {renderSummaryTable()}
                            {renderPaidSplitTable("Paid vs Not Paid by Segment", "Segment", aggregate.paidBySegmentRows, aggregate.paidSplitTotals)}
                            {renderPaidSplitTable("Paid vs Not Paid by Segment", "Segment", aggregate.paidBySegmentRows, aggregate.paidSplitTotals)}
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, lg: 5 }}>
                        <Stack gap="md">
                            {renderPie("Requests by Status", toPie(aggregate.statusRows), requestsFormatter)}
                            {selectedPortfolio === ALL_PORTFOLIOS
                                ? renderPie("Requests by Portfolio Type", toPie(aggregate.typeRows), requestsFormatter)
                                : renderPie("Requests by Management Type", toPie(aggregate.managementRows), requestsFormatter)}
                            {aggregate.riskRows.length > 0 && renderPie("Requests by Risk Level", toPie(aggregate.riskRows), requestsFormatter)}
                            {renderPie("Requests by Submission Method", [
                                { id: 0, value: aggregate.totals.digitalRequests, label: "Digital" },
                                { id: 1, value: aggregate.totals.manualRequests, label: "Manual" },
                            ], requestsFormatter)}
                            {renderPie("Clients by Stage", [
                                { id: 0, value: aggregate.totals.executedClients, label: "Executed" },
                                { id: 1, value: aggregate.totals.inProgressClients, label: "In progress" },
                                { id: 2, value: aggregate.totals.rejectedClients, label: "Rejected" },
                            ], clientsFormatter)}
                        </Stack>
                    </Grid.Col>
                </Grid>
            </Stack>
        </Stack>
    );
};

export default PortfolioRequestsProgress;
